import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import {
    createAdminAlloVoisinsSavedMessage,
    deleteAdminAlloVoisinsSavedMessage,
    getAdminAlloVoisinsConversation,
    getAdminAlloVoisinsConversationByChannelId,
    getAdminAlloVoisinsMessages,
    getAdminAlloVoisinsSavedMessages,
    sendAdminAlloVoisinsMessage,
    updateAdminAlloVoisinsSavedMessage
} from "../../../data/admin/allovoisinsMessages";

const PAGE_SIZE = 100;
const DEFAULT_SAVED_MESSAGE_FORM = {
    title: "",
    message: "",
    sortOrder: 0,
    isActive: true
};

export async function loader() {
    const [data, savedMessages] = await Promise.all([
        getAdminAlloVoisinsMessages(0, PAGE_SIZE),
        getAdminAlloVoisinsSavedMessages()
    ]);
    return { data, savedMessages };
}

function formatDateTime(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString("fr-FR", {
        dateStyle: "short",
        timeStyle: "short"
    });
}

function formatOwnerLabel(conversation) {
    const fullName = `${conversation.ownerFirstname || ""} ${conversation.ownerLastname || ""}`.trim();
    return fullName || conversation.ownerEmail || `Utilisateur #${conversation.ownerUserId}`;
}

function truncate(value, maxLength = 110) {
    if (!value) return "";
    return value.length <= maxLength ? value : `${value.slice(0, maxLength - 1)}...`;
}

function sortConversations(conversations) {
    return [...(conversations ?? [])].sort((a, b) => {
        const aDate = new Date(a.lastMessageAt || a.createdAt).getTime();
        const bDate = new Date(b.lastMessageAt || b.createdAt).getTime();
        return bDate - aDate;
    });
}

function mergeConversations(existingConversations, incomingConversations) {
    const conversationsById = new Map();

    for (const conversation of existingConversations ?? []) {
        conversationsById.set(conversation.chatId, conversation);
    }

    for (const conversation of incomingConversations ?? []) {
        conversationsById.set(conversation.chatId, conversation);
    }

    return sortConversations(Array.from(conversationsById.values()));
}

function sortSavedMessages(savedMessages) {
    return [...(savedMessages ?? [])].sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) {
            return a.sortOrder - b.sortOrder;
        }

        return a.title.localeCompare(b.title, "fr");
    });
}

function ConversationButton({ conversation, loaded, selected, onSelect }) {
    const ownerLabel = formatOwnerLabel(conversation);
    const lastDate = conversation.lastMessageAt || conversation.createdAt;

    return (
        <button
            type="button"
            className={`admin-av-conversation ${selected ? "is-selected" : ""}`}
            onClick={() => onSelect(conversation)}
        >
            <span className="admin-av-conversation-topline">
                <strong>{conversation.toolName}</strong>
                <span>{formatDateTime(lastDate)}</span>
            </span>
            <span className="admin-av-conversation-owner">{ownerLabel}</span>
            <span className="admin-av-conversation-preview">
                {conversation.loadError || truncate(conversation.lastMessagePreview) || (loaded ? "Aucun message texte" : "Ouvrir pour charger les messages")}
            </span>
            <span className="admin-av-conversation-meta">
                <span>{conversation.status}</span>
                {loaded ? (
                    <span>{conversation.messageCount.toLocaleString("fr-FR")} message{conversation.messageCount > 1 ? "s" : ""}</span>
                ) : (
                    <span>Non chargé</span>
                )}
            </span>
        </button>
    );
}

export default function AdminAlloVoisinsMessagesPage() {
    const { data: initialData, savedMessages: initialSavedMessages } = useLoaderData();
    const { channelId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(initialData);
    const [savedMessages, setSavedMessages] = useState(sortSavedMessages(initialSavedMessages));
    const [savedMessageForm, setSavedMessageForm] = useState(DEFAULT_SAVED_MESSAGE_FORM);
    const [editingSavedMessageId, setEditingSavedMessageId] = useState(null);
    const [savingSavedMessage, setSavingSavedMessage] = useState(false);
    const [deletingSavedMessageId, setDeletingSavedMessageId] = useState(null);
    const [savedMessageError, setSavedMessageError] = useState("");
    const [nextSkip, setNextSkip] = useState(initialData.conversations?.length ?? 0);
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingSelectedChannel, setLoadingSelectedChannel] = useState(false);
    const [loadingChatId, setLoadingChatId] = useState(null);
    const [loadedChatIds, setLoadedChatIds] = useState(new Set());
    const [error, setError] = useState("");
    const lastSyncedChatIdRef = useRef(null);

    const conversations = useMemo(() => sortConversations(data.conversations), [data.conversations]);
    const activeSavedMessages = useMemo(() => savedMessages.filter((item) => item.isActive), [savedMessages]);
    const selectedChannelId = channelId ? decodeURIComponent(channelId) : null;
    const selectedConversation = selectedChannelId
        ? conversations.find((item) => item.channelId === selectedChannelId) ?? null
        : null;
    const selectedChatId = selectedConversation?.chatId ?? null;
    const selectedConversationLoaded = selectedConversation ? loadedChatIds.has(selectedConversation.chatId) : false;
    const hasMoreConversations = nextSkip < data.totalChats;

    const selectConversation = useCallback((conversation) => {
        navigate(`/administration/messages-allovoisins/${encodeURIComponent(conversation.channelId)}`);
    }, [navigate]);

    const replaceConversation = useCallback((updatedConversation) => {
        setData((current) => ({
            ...current,
            conversations: mergeConversations(current.conversations, [updatedConversation])
        }));
    }, []);

    useEffect(() => {
        if (!selectedChannelId || selectedConversation) {
            return;
        }

        let cancelled = false;

        const loadConversationByChannelId = async () => {
            setLoadingSelectedChannel(true);
            setError("");

            try {
                const conversation = await getAdminAlloVoisinsConversationByChannelId(selectedChannelId);
                if (cancelled) return;

                lastSyncedChatIdRef.current = conversation.chatId;
                replaceConversation(conversation);
                setLoadedChatIds((current) => new Set(current).add(conversation.chatId));
            } catch (loadError) {
                if (!cancelled) {
                    setError(loadError.message || "Impossible de charger cette conversation AlloVoisins.");
                }
            } finally {
                if (!cancelled) {
                    setLoadingSelectedChannel(false);
                }
            }
        };

        loadConversationByChannelId();

        return () => {
            cancelled = true;
        };
    }, [replaceConversation, selectedChannelId, selectedConversation]);

    useEffect(() => {
        if (!selectedChatId || lastSyncedChatIdRef.current === selectedChatId) {
            return;
        }

        let cancelled = false;
        const chatId = selectedChatId;
        lastSyncedChatIdRef.current = chatId;

        const loadConversation = async () => {
            setLoadingChatId(chatId);
            setError("");

            try {
                const conversation = await getAdminAlloVoisinsConversation(chatId);
                if (cancelled) return;

                replaceConversation(conversation);
                setLoadedChatIds((current) => new Set(current).add(chatId));
            } catch (loadError) {
                if (!cancelled) {
                    lastSyncedChatIdRef.current = null;
                    setError(loadError.message || "Impossible de charger cette conversation AlloVoisins.");
                }
            } finally {
                if (!cancelled) {
                    setLoadingChatId(null);
                }
            }
        };

        loadConversation();

        return () => {
            cancelled = true;
        };
    }, [replaceConversation, selectedChatId]);

    const loadMoreConversations = useCallback(async () => {
        if (loadingMore || !hasMoreConversations) {
            return;
        }

        setLoadingMore(true);
        setError("");

        try {
            const nextPage = await getAdminAlloVoisinsMessages(nextSkip, PAGE_SIZE);
            setData((current) => ({
                ...current,
                generatedAtUtc: nextPage.generatedAtUtc,
                totalChats: nextPage.totalChats,
                conversations: mergeConversations(current.conversations, nextPage.conversations)
            }));
            setNextSkip((current) => current + (nextPage.conversations?.length ?? 0));
        } catch (loadError) {
            setError(loadError.message || "Impossible de charger plus de conversations AlloVoisins.");
        } finally {
            setLoadingMore(false);
        }
    }, [hasMoreConversations, loadingMore, nextSkip]);

    const handleConversationListScroll = useCallback((event) => {
        const target = event.currentTarget;
        const remainingScroll = target.scrollHeight - target.scrollTop - target.clientHeight;

        if (remainingScroll < 180) {
            loadMoreConversations();
        }
    }, [loadMoreConversations]);

    const refresh = async () => {
        setRefreshing(true);
        setError("");

        try {
            const refreshed = await getAdminAlloVoisinsMessages();
            setData(refreshed);
            setNextSkip(refreshed.conversations?.length ?? 0);
            setLoadedChatIds(new Set());
            lastSyncedChatIdRef.current = null;
        } catch (refreshError) {
            setError(refreshError.message || "Impossible d'actualiser les messages AlloVoisins.");
        } finally {
            setRefreshing(false);
        }
    };

    const sendMessage = async () => {
        if (!selectedConversation || !message.trim()) return;

        setSending(true);
        setError("");

        try {
            const updatedConversation = await sendAdminAlloVoisinsMessage(selectedConversation.chatId, message);
            replaceConversation(updatedConversation);
            setLoadedChatIds((current) => new Set(current).add(updatedConversation.chatId));
            navigate(`/administration/messages-allovoisins/${encodeURIComponent(updatedConversation.channelId)}`);
            setMessage("");
        } catch (sendError) {
            setError(sendError.message || "Impossible d'envoyer le message sur AlloVoisins.");
        } finally {
            setSending(false);
        }
    };

    const resetSavedMessageForm = () => {
        setSavedMessageForm(DEFAULT_SAVED_MESSAGE_FORM);
        setEditingSavedMessageId(null);
        setSavedMessageError("");
    };

    const startEditingSavedMessage = (savedMessage) => {
        setSavedMessageForm({
            title: savedMessage.title,
            message: savedMessage.message,
            sortOrder: savedMessage.sortOrder,
            isActive: savedMessage.isActive
        });
        setEditingSavedMessageId(savedMessage.id);
        setSavedMessageError("");
    };

    const saveSavedMessage = async () => {
        const payload = {
            title: savedMessageForm.title,
            message: savedMessageForm.message,
            sortOrder: Number(savedMessageForm.sortOrder) || 0,
            isActive: savedMessageForm.isActive
        };

        if (!payload.title.trim() || !payload.message.trim()) {
            setSavedMessageError("Le titre et le message sont obligatoires.");
            return;
        }

        setSavingSavedMessage(true);
        setSavedMessageError("");

        try {
            const savedMessage = editingSavedMessageId
                ? await updateAdminAlloVoisinsSavedMessage(editingSavedMessageId, payload)
                : await createAdminAlloVoisinsSavedMessage(payload);

            setSavedMessages((current) => {
                const withoutUpdated = current.filter((item) => item.id !== savedMessage.id);
                return sortSavedMessages([...withoutUpdated, savedMessage]);
            });
            resetSavedMessageForm();
        } catch (saveError) {
            setSavedMessageError(saveError.message || "Impossible d'enregistrer ce message préenregistré.");
        } finally {
            setSavingSavedMessage(false);
        }
    };

    const deleteSavedMessage = async (savedMessageId) => {
        if (!window.confirm("Supprimer ce message préenregistré ?")) {
            return;
        }

        setDeletingSavedMessageId(savedMessageId);
        setSavedMessageError("");

        try {
            await deleteAdminAlloVoisinsSavedMessage(savedMessageId);
            setSavedMessages((current) => current.filter((item) => item.id !== savedMessageId));
            if (editingSavedMessageId === savedMessageId) {
                resetSavedMessageForm();
            }
        } catch (deleteError) {
            setSavedMessageError(deleteError.message || "Impossible de supprimer ce message préenregistré.");
        } finally {
            setDeletingSavedMessageId(null);
        }
    };

    const applySavedMessage = (savedMessage) => {
        setMessage(savedMessage.message);
    };

    return (
        <div className="admin-av-page">
            <div className="admin-av-header">
                <div>
                    <h1>Messages AlloVoisins</h1>
                    <p>Mise à jour : {formatDateTime(data.generatedAtUtc)} - {conversations.length.toLocaleString("fr-FR")} / {data.totalChats.toLocaleString("fr-FR")} conversation{data.totalChats > 1 ? "s" : ""}</p>
                </div>
                <Button
                    type="button"
                    icon="pi pi-refresh"
                    label="Actualiser"
                    size="small"
                    loading={refreshing}
                    onClick={refresh}
                />
            </div>

            {error ? <p className="admin-av-error">{error}</p> : null}

            <div className="admin-av-layout">
                <aside className="admin-av-list" aria-label="Conversations AlloVoisins" onScroll={handleConversationListScroll}>
                    {conversations.length === 0 ? (
                        <p className="admin-av-empty">Aucune conversation AlloVoisins.</p>
                    ) : (
                        <>
                            {conversations.map((conversation) => (
                                <ConversationButton
                                    key={conversation.chatId}
                                    conversation={conversation}
                                    loaded={loadedChatIds.has(conversation.chatId)}
                                    selected={selectedConversation?.chatId === conversation.chatId}
                                    onSelect={selectConversation}
                                />
                            ))}
                            {loadingMore ? (
                                <p className="admin-av-empty">Chargement...</p>
                            ) : hasMoreConversations ? (
                                <Button
                                    type="button"
                                    icon="pi pi-chevron-down"
                                    label="Charger plus"
                                    text
                                    onClick={loadMoreConversations}
                                />
                            ) : null}
                        </>
                    )}
                </aside>

                <section className="admin-av-chat">
                    {selectedConversation ? (
                        <>
                            <header className="admin-av-chat-header">
                                <div>
                                    <h2>{selectedConversation.toolName}</h2>
                                    <div className="admin-av-chat-subtitle">
                                        <a href={`/user/${selectedConversation.ownerUserId}`}>{formatOwnerLabel(selectedConversation)}</a>
                                        <span>Compte : {selectedConversation.alloVoisinsAccountEmail}</span>
                                        <span>Channel : {selectedConversation.channelId}</span>
                                    </div>
                                </div>
                                {selectedConversation.toolCustomUrl ? (
                                    <a className="admin-av-tool-link" href={`/outil/${selectedConversation.toolCustomUrl}`}>
                                        Outil
                                    </a>
                                ) : null}
                            </header>

                            {selectedConversation.loadError ? (
                                <p className="admin-av-error">{selectedConversation.loadError}</p>
                            ) : null}

                            <div className="admin-av-thread">
                                {loadingChatId === selectedConversation.chatId ? (
                                    <p className="admin-av-empty">Chargement de la conversation...</p>
                                ) : selectedConversation.messages.length === 0 ? (
                                    <p className="admin-av-empty">Aucun message texte dans cette conversation.</p>
                                ) : selectedConversation.messages.map((item) => (
                                    <article
                                        key={item.messageId}
                                        className={`admin-av-message ${item.isFromOwner ? "is-owner" : "is-client"}`}
                                    >
                                        <div className="admin-av-message-meta">
                                            <span>{item.senderLabel}</span>
                                            <time dateTime={item.sentAt}>{formatDateTime(item.sentAt)}</time>
                                        </div>
                                        <p>{item.content}</p>
                                    </article>
                                ))}
                            </div>

                            {activeSavedMessages.length > 0 ? (
                                <div className="admin-av-saved-replies">
                                    <span>
                                        <i className="pi pi-bolt" aria-hidden="true"></i>
                                        Réponses rapides
                                    </span>
                                    <div className="admin-av-saved-replies-list">
                                        {activeSavedMessages.map((savedMessage) => (
                                            <Button
                                                key={savedMessage.id}
                                                type="button"
                                                icon="pi pi-plus"
                                                label={savedMessage.title}
                                                className="admin-av-saved-reply-button"
                                                severity="secondary"
                                                size="small"
                                                outlined
                                                disabled={sending}
                                                onClick={() => applySavedMessage(savedMessage)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            <div className="admin-av-composer">
                                <InputTextarea
                                    value={message}
                                    rows={3}
                                    autoResize
                                    disabled={sending}
                                    onChange={(event) => setMessage(event.target.value)}
                                    placeholder="Réponse à envoyer sur AlloVoisins"
                                />
                                <Button
                                    type="button"
                                    icon="pi pi-send"
                                    label="Envoyer"
                                    loading={sending}
                                    disabled={!message.trim() || !selectedConversationLoaded || Boolean(selectedConversation.loadError)}
                                    onClick={sendMessage}
                                />
                            </div>
                        </>
                    ) : loadingSelectedChannel ? (
                        <p className="admin-av-empty">Chargement de la conversation...</p>
                    ) : (
                        <p className="admin-av-empty">Sélectionnez une conversation pour charger son historique local.</p>
                    )}
                </section>
            </div>

            <section className="admin-av-saved-manager">
                <div className="admin-av-saved-manager-header">
                    <div>
                        <h2>Messages préenregistrés</h2>
                        <p>{savedMessages.length.toLocaleString("fr-FR")} message{savedMessages.length > 1 ? "s" : ""}</p>
                    </div>
                    {editingSavedMessageId ? (
                        <Button
                            type="button"
                            icon="pi pi-times"
                            label="Annuler"
                            text
                            onClick={resetSavedMessageForm}
                        />
                    ) : null}
                </div>

                {savedMessageError ? <p className="admin-av-error">{savedMessageError}</p> : null}

                <div className="admin-av-saved-manager-grid">
                    <div className="admin-av-saved-form">
                        <div className="admin-av-saved-form-heading">
                            <h3>{editingSavedMessageId ? "Modifier" : "Nouveau message"}</h3>
                        </div>

                        <label>
                            <span>Titre</span>
                            <InputText
                                value={savedMessageForm.title}
                                disabled={savingSavedMessage}
                                onChange={(event) => setSavedMessageForm((current) => ({ ...current, title: event.target.value }))}
                                placeholder="Ex : Disponibilités"
                            />
                        </label>

                        <label>
                            <span>Message</span>
                            <InputTextarea
                                value={savedMessageForm.message}
                                rows={4}
                                autoResize
                                disabled={savingSavedMessage}
                                onChange={(event) => setSavedMessageForm((current) => ({ ...current, message: event.target.value }))}
                                placeholder="Texte à proposer dans la conversation"
                            />
                        </label>

                        <div className="admin-av-saved-form-row">
                            <label>
                                <span>Ordre</span>
                                <InputText
                                    type="number"
                                    value={savedMessageForm.sortOrder}
                                    disabled={savingSavedMessage}
                                    onChange={(event) => setSavedMessageForm((current) => ({ ...current, sortOrder: event.target.value }))}
                                />
                            </label>
                            <label className="admin-av-saved-checkbox">
                                <input
                                    type="checkbox"
                                    checked={savedMessageForm.isActive}
                                    disabled={savingSavedMessage}
                                    onChange={(event) => setSavedMessageForm((current) => ({ ...current, isActive: event.target.checked }))}
                                />
                                <span>Actif dans le chat</span>
                            </label>
                        </div>

                        <Button
                            type="button"
                            icon="pi pi-save"
                            label={editingSavedMessageId ? "Enregistrer" : "Ajouter"}
                            loading={savingSavedMessage}
                            onClick={saveSavedMessage}
                        />
                    </div>

                    <div className="admin-av-saved-list">
                        {savedMessages.length === 0 ? (
                            <p className="admin-av-empty">Aucun message préenregistré.</p>
                        ) : savedMessages.map((savedMessage) => (
                            <article key={savedMessage.id} className={`admin-av-saved-item ${savedMessage.isActive ? "" : "is-inactive"}`}>
                                <div className="admin-av-saved-item-main">
                                    <div className="admin-av-saved-item-title-row">
                                        <h3>{savedMessage.title}</h3>
                                        <span className={`admin-av-saved-status ${savedMessage.isActive ? "is-active" : "is-inactive"}`}>
                                            {savedMessage.isActive ? "Actif" : "Inactif"}
                                        </span>
                                    </div>
                                    <p>{truncate(savedMessage.message, 180)}</p>
                                    <span className="admin-av-saved-order">Ordre {savedMessage.sortOrder}</span>
                                </div>
                                <div className="admin-av-saved-actions">
                                    <Button
                                        type="button"
                                        icon="pi pi-pencil"
                                        className="admin-av-icon-button"
                                        aria-label="Modifier"
                                        text
                                        onClick={() => startEditingSavedMessage(savedMessage)}
                                    />
                                    <Button
                                        type="button"
                                        icon="pi pi-trash"
                                        className="admin-av-icon-button"
                                        aria-label="Supprimer"
                                        severity="danger"
                                        text
                                        loading={deletingSavedMessageId === savedMessage.id}
                                        onClick={() => deleteSavedMessage(savedMessage.id)}
                                    />
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
